import React, { useState, useEffect } from "react";
import {
  Box,
  Grid2,
  Card,
  CardContent,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  People as PeopleIcon,
  LocationOn as LocationIcon,
  DynamicForm as FormIcon,
  Assignment as SubmissionIcon,
  Approval as ApprovalIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useUserGroups } from "../../hooks/useUserGroups";
import {
  GET_EMPLOYEE_COUNT,
  GET_LOCATION_COUNT,
  GET_FORM_COUNT,
  GET_SUBMISSION_COUNT,
  GET_ALL_PENDING_SUBMISSIONS_COUNT,
  GET_PENDING_SUBMISSIONS_COUNT,
  GET_PENDING_APPROVALS_COUNT,
} from "../../graphql/operations";

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactElement;
  loading: boolean;
  error?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  loading,
  error,
}) => (
  <Card>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography color="textSecondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" component="div">
            {loading ? "..." : error ? "N/A" : value}
          </Typography>
          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
        </Box>
        <Box color="primary.main">
          {React.cloneElement(icon, { fontSize: "large" })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, loading: groupsLoading } = useUserGroups();
  const [stats, setStats] = useState({
    employeeCount: 0,
    locationCount: 0,
    formCount: 0,
    submissionCount: 0,
    pendingSubmissionCount: 0,
    pendingApprovalCount: 0,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  // Admin-only queries - will fail for non-admin users
  const { data: employeeData, error: employeeError } = useQuery(
    GET_EMPLOYEE_COUNT,
    {
      skip: !isAdmin || groupsLoading,
    }
  );

  const { data: locationData, error: locationError } = useQuery(
    GET_LOCATION_COUNT,
    {
      skip: !isAdmin || groupsLoading,
    }
  );

  const { data: formData, error: formError } = useQuery(GET_FORM_COUNT, {
    skip: !isAdmin || groupsLoading,
  });

  // This query should work for all authenticated users
  const { data: submissionData, error: submissionError } =
    useQuery(GET_SUBMISSION_COUNT);

  const pendingSubmissionCountQuery = isAdmin
    ? GET_ALL_PENDING_SUBMISSIONS_COUNT
    : GET_PENDING_SUBMISSIONS_COUNT;

  const { data: pendingSubmissionData } = useQuery(pendingSubmissionCountQuery);

  const { data: pendingApprovalData } = useQuery(GET_PENDING_APPROVALS_COUNT, {
    skip: !isAdmin || groupsLoading,
  });

  useEffect(() => {
    if (!groupsLoading) {
      setLoading(false);

      // Update stats with data or handle errors
      setStats({
        employeeCount: employeeData?.getEmployeeCount || 0,
        locationCount: locationData?.getLocationCount || 0,
        formCount: formData?.getFormCount || 0,
        submissionCount: submissionData?.getSubmissionCount || 0,
        pendingSubmissionCount:
          pendingSubmissionData?.getPendingSubmissionsCount ||
          pendingSubmissionData?.getAllPendingSubmissionsCount ||
          0,
        pendingApprovalCount:
          pendingApprovalData?.getPendingApprovalsCount || 0,
      });

      // Set errors for unauthorized queries
      const newErrors: Record<string, string> = {};
      if (employeeError) {
        newErrors.employee = isAdmin
          ? "Error loading employees"
          : "Access denied";
      }
      if (locationError) {
        newErrors.location = isAdmin
          ? "Error loading locations"
          : "Access denied";
      }
      if (formError) {
        newErrors.form = isAdmin ? "Error loading forms" : "Access denied";
      }
      if (submissionError) {
        newErrors.submission = "Error loading submissions";
      }
      setErrors(newErrors);
    }
  }, [
    employeeData,
    locationData,
    formData,
    submissionData,
    employeeError,
    locationError,
    formError,
    submissionError,
    groupsLoading,
    isAdmin,
  ]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      {isAdmin ? (
        <Typography variant="h6" color="textSecondary">
          Administrator Dashboard - Full Access
        </Typography>
      ) : (
        <Typography variant="h6" color="textSecondary">
          User Dashboard
        </Typography>
      )}
      {/* 
      {!isAdmin && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Welcome! You have limited access as a regular user.
        </Alert>
      )} */}

      <Grid2 container spacing={3}>
        {isAdmin && (
          <Grid2 size={4} onClick={() => navigate("/dashboard/employees")}>
            <StatCard
              title="Employees"
              value={stats.employeeCount}
              icon={<PeopleIcon />}
              loading={groupsLoading}
              error={errors.employee}
            />
          </Grid2>
        )}

        {isAdmin && (
          <Grid2 size={4} onClick={() => navigate("/dashboard/locations")}>
            <StatCard
              title="Locations"
              value={stats.locationCount}
              icon={<LocationIcon />}
              loading={groupsLoading}
              error={errors.location}
            />
          </Grid2>
        )}

        {isAdmin && (
          <Grid2 size={4} onClick={() => navigate("/dashboard/forms")}>
            <StatCard
              title="Forms"
              value={stats.formCount}
              icon={<FormIcon />}
              loading={groupsLoading}
              error={errors.form}
            />
          </Grid2>
        )}

        <Grid2 size={4} onClick={() => navigate("/dashboard/submissions")}>
          <StatCard
            title="Submissions"
            value={stats.submissionCount}
            icon={<SubmissionIcon />}
            loading={false} // This query runs for all users
            error={errors.submission}
          />
        </Grid2>

        <Grid2 size={4} onClick={() => navigate("/dashboard/submissions")}>
          <StatCard
            title="Submissions Due"
            value={stats.pendingSubmissionCount}
            icon={<SubmissionIcon />}
            loading={false} // This query runs for all users
            error={errors.submission}
          />
        </Grid2>

        {isAdmin && (
          <Grid2 size={4} onClick={() => navigate("/dashboard/submissions")}>
            <StatCard
              title="Approvals Due"
              value={stats.pendingApprovalCount}
              icon={<ApprovalIcon />}
              loading={false} // This query runs for all users
              error={errors.submission}
            />
          </Grid2>
        )}
      </Grid2>
    </Box>
  );
};

export default DashboardPage;
